import { BlockMath, InlineMath } from 'react-katex';
import 'katex/dist/katex.min.css';

/**
 * Renders text with LaTeX math expressions
 * Supports both display math ($$...$$) and inline math ($...$)
 *
 * @param text - The text containing LaTeX expressions
 * @returns Array of React elements with rendered math
 */
export function renderMathInText(text: string): (string | React.ReactElement)[] {
  const parts: (string | React.ReactElement)[] = [];
  let currentText = text;
  let key = 0;

  // First, split by display math ($$...$$)
  // Use [\s\S] instead of . to match newlines
  const displayMathRegex = /\$\$(.*?)\$\$/gs;
  let lastIndex = 0;
  let match;

  while ((match = displayMathRegex.exec(currentText)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      const textBefore = currentText.slice(lastIndex, match.index);
      parts.push(
        <span key={key++}>{renderInlineMath(textBefore, key)}</span>
      );
    }

    // Add the display math
    parts.push(<BlockMath key={key++} math={match[1]} />);

    lastIndex = displayMathRegex.lastIndex;
  }

  // Add remaining text
  if (lastIndex < currentText.length) {
    const remaining = currentText.slice(lastIndex);
    parts.push(<span key={key++}>{renderInlineMath(remaining, key)}</span>);
  }

  return parts;
}

/**
 * Renders inline math within text
 *
 * @param text - The text containing inline math ($...$)
 * @param startKey - Starting key for React elements
 * @returns Array of strings and React elements
 */
function renderInlineMath(text: string, startKey: number): (string | React.ReactElement)[] {
  const parts: (string | React.ReactElement)[] = [];
  // Use [\s\S] instead of . to match newlines (though inline math usually shouldn't span lines, it's safer)
  const inlineMathRegex = /\$(.*?)\$/gs;
  let lastIndex = 0;
  let match;
  let key = startKey;

  while ((match = inlineMathRegex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    // Add the inline math
    parts.push(<InlineMath key={key++} math={match[1]} />);

    lastIndex = inlineMathRegex.lastIndex;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}
